/**
 * Willtec Announcement Carousel Custom Element
 * Handles a accessible, loopable carousel for the announcement bar without external libraries.
 */
(() => {
  if (customElements.get('announcement-carousel')) return;

  class AnnouncementCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isPaused = false;
  }

  connectedCallback() {
    this.slidesContainer = this.querySelector('[data-slides-container]');
    this.slides = this.querySelectorAll('[data-slide]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    
    if (this.slides.length <= 1) return;

    const autoplayAttr = this.getAttribute('autoplay');
    this.autoplay = autoplayAttr === 'true' || autoplayAttr === '1' || autoplayAttr === '';
    this.interval = parseInt(this.getAttribute('interval'), 10) || 5000;
    const pauseAttr = this.getAttribute('pause-on-hover');
    this.pauseOnHover = pauseAttr === 'true' || pauseAttr === '1' || pauseAttr === '';

    // Init controls
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.handlePrev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.handleNext());

    // Init hover/focus pause
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.handleMouseEnter());
      this.addEventListener('mouseleave', () => this.handleMouseLeave());
    }

    this.addEventListener('focusin', () => this.handleFocusIn());
    this.addEventListener('focusout', () => this.handleFocusOut());

    // Handle visibility changes
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());

    // Handle swipe support
    this.initTouch();

    // Start autoplay
    this.startAutoplay();
    this.updateAccessibility();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  startAutoplay() {
    if (!this.autoplay || this.slides.length <= 1) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (!this.isPaused) {
        this.next();
      }
    }, this.interval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    this.startAutoplay();
  }

  goTo(index) {
    if (this.slides.length <= 1) return;
    
    const total = this.slides.length;
    const currentSlide = this.slides[this.currentIndex];
    currentSlide.classList.remove('active');
    currentSlide.setAttribute('aria-hidden', 'true');
    currentSlide.style.opacity = '0';
    currentSlide.style.pointerEvents = 'none';
    
    // Manage loop boundaries
    if (index >= total) {
      this.currentIndex = 0;
    } else if (index < 0) {
      this.currentIndex = total - 1;
    } else {
      this.currentIndex = index;
    }

    const nextSlide = this.slides[this.currentIndex];
    nextSlide.classList.add('active');
    nextSlide.removeAttribute('aria-hidden');
    nextSlide.style.opacity = '1';
    nextSlide.style.pointerEvents = 'auto';

    this.updateAccessibility();
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  handleNext() {
    this.next();
    this.resetAutoplay();
  }

  handlePrev() {
    this.prev();
    this.resetAutoplay();
  }

  handleMouseEnter() {
    this.isPaused = true;
  }

  handleMouseLeave() {
    this.isPaused = false;
  }

  handleFocusIn() {
    this.isPaused = true;
  }

  handleFocusOut() {
    this.isPaused = false;
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  updateAccessibility() {
    this.slides.forEach((slide, idx) => {
      if (idx === this.currentIndex) {
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('tabindex');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');
      }
    });
  }

  // Handle CSS prefers-reduced-motion check
  isMotionReduced() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    return mediaQuery && mediaQuery.matches;
  }

  initTouch() {
    let startX = 0;
    let endX = 0;
    
    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.handleNext();
        } else {
          this.handlePrev();
        }
      }
    }, { passive: true });
  }

  // Hook for Shopify Theme Editor selection events
  onBlockSelect(blockId) {
    const slideToSelect = this.querySelector(`[data-block-id="${blockId}"]`);
    if (slideToSelect) {
      const index = Array.from(this.slides).indexOf(slideToSelect);
      if (index !== -1) {
        this.goTo(index);
        this.stopAutoplay(); // pause autoplay temporarily when customization is happening
      }
    }
  }

  onBlockDeselect() {
    this.startAutoplay();
  }
}

  if (!customElements.get('announcement-carousel')) {
    customElements.define('announcement-carousel', AnnouncementCarousel);
  }
})();
